import { cn, mergeClasses } from '#lib/helpers/class.js';
import React from 'react';
import { DragHandleContext, DragHandle } from './sortable.jsx';
import { AddButton } from '#components/row-buttons.jsx';
import FloatingTooltip from '#components/tooltip.jsx';
import { Icon } from '@iconify-icon/react';

export function RowTitle(props) {
    const { children, className, ...rest } = props;
    return (
        <p
            className={mergeClasses('whitespace-nowrap text-ellipsis overflow-hidden w-0 flex-grow', className)}
            {...rest}
        >
            {children}
        </p>
    );
}

export function RowPlaceholder(props) {
    const { children, className, ...rest } = props;
    return (
        <p
            className={mergeClasses(
                'whitespace-nowrap text-ellipsis overflow-hidden text-neutral-400 w-0 flex-grow',
                className
            )}
            {...rest}
        >
            {children}
        </p>
    );
}

export function RowNumber(props) {
    const { children, ...rest } = props;
    const selected = useContext();
    return (
        <span
            className={cn(
                selected
                    ? 'border-primary-400 bg-primary-500 text-white'
                    : 'bg-neutral-50 border-neutral-200 text-neutral-500',
                'h-10 w-10 flex-shrink-0 rounded flex items-center justify-center font-semibold border'
            )}
            {...rest}
        >
            {children}
        </span>
    );
}

export function RowButtons(props) {
    const {
        rooms,
        hasChild,
        parentId,
        selfId,
        selected,
        onDelete,
        overlay,
        invalid,
        incoming,
        isDragging,
        isDeletable = true,
        edit,
        onEdit,
        onClone
    } = props;
    const dragHandleProps = React.useContext(DragHandleContext);

    if (overlay) return null;
    return (
        <div className="flex items-center gap-x-[6px]">
            {incoming && (
                <FloatingTooltip content={_t.edit.planner.addPlan()} placement="top">
                    <Icon
                        className="cursor-pointer pt-[4px]"
                        icon="material-symbols-light:add"
                        width="1.4rem"
                        height="1.4rem"
                    />
                </FloatingTooltip>
            )}
            {invalid && (
                <FloatingTooltip content={_t.global.invalidFieldInside()} placement="top">
                    <Icon
                        icon="ic:round-error-outline"
                        width="1.2rem"
                        height="1.2rem"
                        className="mt-[6px] text-red-500"
                    />
                </FloatingTooltip>
            )}
            {rooms && rooms.length > 0 && (
                <FloatingTooltip content={`${rooms.length} ${rooms.length > 1 ? 'rooms' : 'room'}`} placement="top">
                    <div className="w-5 h-5 relative bg-neutral-200 border border-neutral-300 rounded-[50%] cursor-default">
                        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-bold">
                            {rooms.length}
                        </span>
                    </div>
                </FloatingTooltip>
            )}
            {edit && (
                <FloatingTooltip content={_t.global.edit()} placement="top">
                    <Icon
                        icon="material-symbols:edit-outline-rounded"
                        width="1.3rem"
                        height="1.3rem"
                        className="cursor-pointer pt-2"
                        onClick={onEdit}
                    />
                </FloatingTooltip>
            )}

            {isDeletable && (
                <FloatingTooltip content={_t.global.delete()} placement="top">
                    <Icon
                        icon="ph:trash"
                        width="1.3rem"
                        height="1.3rem"
                        className="cursor-pointer pt-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (hasChild) onDelete(selfId);
                            else onDelete(parentId, selfId);
                        }}
                    />
                </FloatingTooltip>
            )}
            {isDragging && (
                <FloatingTooltip content={_t.global.drag()} placement="top">
                    <DragHandle className="rounded cursor-grab pt-2" selected={selected} {...dragHandleProps} />
                </FloatingTooltip>
            )}
            {onClone && (
                <FloatingTooltip content={_t.global.clone()} placement="top">
                    <Icon
                        icon="material-symbols:content-copy-outline-rounded"
                        width="1.3rem"
                        height="1.3rem"
                        className="cursor-pointer pt-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClone(selfId);
                        }}
                    />
                </FloatingTooltip>
            )}
        </div>
    );
}

function _Row(props, ref) {
    const { as = 'li', selected, className, children, ...rest } = props;
    return React.createElement(
        as,
        {
            ref,
            className: mergeClasses('gap-1 flex items-center -mt-px cursor-default', className),
            ...rest
        },
        <Context.Provider value={selected}>{children}</Context.Provider>
    );
}

export const Row = React.forwardRef(_Row);

export function AddRowButton(props) {
    const { children, className, ...rest } = props;
    return (
        <AddButton
            className={mergeClasses(
                'relative w-full flex justify-center items-center gap-x-2 px-4 py-1 mb-0 text-neutral-600 rounded border-2 border-dashed border-neutral-300 hover:bg-white hover:border-solid cursor-pointer',
                className
            )}
            {...rest}
        >
            <div className="h-full flex items-center">{children || _t.edit.planner.new()}</div>
            <div className="h-full flex items-center">
                <Icon icon="material-symbols:add" width="1.2rem" height="1.2rem" className="translate-y-0.5" />
            </div>
        </AddButton>
    );
}

export function GetCitiesButton(props) {
    const { children, className, ...rest } = props;
    return (
        <AddButton
            className={mergeClasses(
                'relative w-full flex justify-center items-center gap-x-2 px-4 py-1 mb-0 text-neutral-600 rounded border-2 border-dashed border-neutral-300 hover:bg-white hover:border-solid cursor-pointer',
                className
            )}
            {...rest}
        >
            <div className="h-full flex items-center">{children || _t.edit.planner.new()}</div>
            <div className="h-full flex items-center">
                <Icon icon="ph:city-light" height="1.3rem" width="1.3rem" />
            </div>
        </AddButton>
    );
}

const Context = React.createContext();
const useContext = () => React.useContext(Context);
